import type { QueryClient } from '@tanstack/react-query';

/**
 * Query keys for the people screens, and what to refresh after a change.
 *
 * A change to one person shows up in more places than the screen it was made
 * on: the agent pickers on the ticket list, the group cards, the workload bars,
 * the assignee choices in every form. The old screen refreshed only its own
 * list, so a deactivated agent went on being offered in "Add somebody" and
 * "Escalate to" for five minutes, and a merged account's panel kept showing
 * the groups that had just moved away. Every mutation here goes through these
 * two functions instead of naming keys at the call site.
 */

export const peopleKeys = {
  list: (params: string) => ['people', 'list', params] as const,
  profile: (id: number) => ['person-profile', id] as const,
  assignableRoles: ['people', 'assignable-roles'] as const,
  workload: ['agent-workload'] as const,
  groups: ['groups'] as const,
  members: (groupId: number) => ['group-members', groupId] as const,
  businessHours: ['admin-business-hours'] as const,
  requesterGroups: ['requester-groups'] as const,
  requesterGroup: (groupId: number) => ['requester-groups', 'detail', groupId] as const,
  requesterGroupMembers: (groupId: number, params: string) =>
    ['requester-groups', 'members', groupId, params] as const,
};

/**
 * After anything that changes a requester group or who is in one. The ticket
 * filter metadata is refreshed too, under every key the screens read it by:
 * the ticket list, the automation and SLA condition builders and the ticket
 * page all offer the groups by name, from `/tickets/filters`.
 */
export function refreshRequesterGroups(queryClient: QueryClient, personIds: number[] = []): void {
  const keys: ReadonlyArray<readonly unknown[]> = [
    peopleKeys.requesterGroups,
    ['ticket-filter-meta'],
    ['resource-schema', '/tickets'],
    ['ticket-detail', 'choices'],
  ];
  for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
  for (const id of personIds) void queryClient.invalidateQueries({ queryKey: peopleKeys.profile(id) });
}

/** After anything that changes who somebody is, or whether they are an agent. */
export function refreshPeople(queryClient: QueryClient, personIds: number[] = []): void {
  const keys: ReadonlyArray<readonly unknown[]> = [
    ['people'],
    peopleKeys.workload,
    // Pickers and lookups that list people or agents, on this and other screens.
    ['directory'],
    ['directory-agents'],
    ['directory-people'],
    ['person-picker'],
    ['lookup'],
    ['palette'],
  ];
  for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
  if (personIds.length === 0) {
    void queryClient.invalidateQueries({ queryKey: ['person-profile'] });
  } else {
    for (const id of personIds) void queryClient.invalidateQueries({ queryKey: peopleKeys.profile(id) });
  }
}

/** After anything that changes a group or who is in it. */
export function refreshGroups(
  queryClient: QueryClient,
  groupIds: number[] = [],
  personIds: number[] = [],
): void {
  const keys: ReadonlyArray<readonly unknown[]> = [
    peopleKeys.groups,
    peopleKeys.workload,
    ['directory', 'groups'],
    ['directory', 'agents'],
    ['directory-groups'],
    ['lookup', 'groups'],
    ['person-picker'],
  ];
  for (const queryKey of keys) void queryClient.invalidateQueries({ queryKey });
  for (const id of groupIds) void queryClient.invalidateQueries({ queryKey: peopleKeys.members(id) });
  for (const id of personIds) void queryClient.invalidateQueries({ queryKey: peopleKeys.profile(id) });
}
